// Full edit window for an already-logged time entry — the counterpart to the
// "Log time" form. Every field the create form collects is editable, plus the
// start/end/duration triad (<TimeRangeFields>).
//
// "Who" is approver-only: an examiner may correct their own entry but may not
// move time onto a colleague. The API enforces the same rule, so hiding the
// control is a courtesy, not the guard.
//
// Billed entries never reach here (the row hides the edit button), but the API
// still answers 409 if one slips through — surfaced below as a clear message.

import { useEffect, useState, type FormEvent } from 'react';
import type { TimeEntry } from '@stfors/shared-types';
import { Modal } from './Modal';
import { ProjectOptions } from './ProjectOptions';
import { TimeRangeFields, type TimeRangeValue } from './TimeRangeFields';
import { MoneyInput } from './MoneyInput';
import { SoldAssetPicker } from './SoldAssetPicker';
import { MatterSelect } from './MatterSelect';
import { SaleDateField, startOfLocalDay } from './SaleDateField';
import { ApiError } from '../api/client';
import { apiErrorText } from '../utils/apiErrors';
import { can } from '../auth/store';
import { useProjects } from '../api/queries/projects';
import { useAssignableUsers } from '../api/queries/project-tasks';
import {
  useServiceRates,
  useUpdateTimeEntry,
  effectiveRates,
} from '../api/queries/time-entries';
import { formatMoney } from '../utils/money';
import { isFlatRate } from '../utils/time-entry';
import './EditTimeEntryModal.css';

interface Props {
  open: boolean;
  entry: TimeEntry;
  onClose: () => void;
}

/** One row of the rate picker — the effective sheet, plus the entry's own rate. */
interface RateOption {
  id: number;
  amount: string;
  rate_type: string;
  is_product_sale: boolean;
  label: string;
}

export function EditTimeEntryModal({ open, entry, onClose }: Props): JSX.Element {
  const update = useUpdateTimeEntry();
  const canReassign = can('time_entry.approve');

  // Seeded from the record verbatim as INITIAL STATE, not from an effect.
  // TimeEntriesPage keys the modal by entry id, so it mounts fresh per entry and
  // this is the only writer for these fields on open. That matters: an
  // effect-based seed ran on the same flush as the default-rate effect below,
  // which still held the pre-seed `rateId` (0) and a rates list fetched with no
  // project_id — so it overwrote the entry's real rate with the catalog's first.
  const [projectId, setProjectId] = useState(entry.project_id);
  const [userId, setUserId] = useState(entry.user_id);
  const [rateId, setRateId] = useState(entry.service_rate_id);
  const [range, setRange] = useState<TimeRangeValue>({
    start: entry.date,
    end: entry.ended_at_epoch ?? null,
    // '' for a flat-fee entry logged without a duration (0057).
    hours: entry.hours ?? '',
  });
  // A flat-fee entry's own fee; the rate's amount stands in when it has none.
  const [fee, setFee] = useState(entry.flat_amount ?? entry.service_rate_amount ?? '');
  // The drive this charge sells, if any (0058).
  const [assetId, setAssetId] = useState<number | null>(entry.asset_id ?? null);
  // The project's matter this work belongs to (0061).
  const [matterId, setMatterId] = useState<number | null>(entry.matter_id ?? null);
  const [description, setDescription] = useState(entry.description);
  const [topError, setTopError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const projectsQ = useProjects();
  const usersQ = useAssignableUsers();
  const ratesQ = useServiceRates(projectId || undefined);
  const sheet = effectiveRates(ratesQ.data?.service_rates ?? []);

  const rateOptions: RateOption[] = sheet.map((r) => ({
    id: r.id,
    amount: r.amount,
    rate_type: r.rate_type,
    is_product_sale: r.is_product_sale,
    label:
      `${r.name}` +
      `${r.project_override_for ? ' (project rate)' : r.client_override_for ? ' (client rate)' : ''}` +
      ` — ${formatMoney(r.amount)}/${r.rate_type === 'hourly' ? 'hr' : 'flat'}`,
  }));

  // The effective sheet collapses the cascade to one rate per name, so the rate
  // this entry was billed at may be absent from it — shadowed by a newer
  // client/project override of the same name, or since deactivated. Keep it
  // listed (marked) instead of dropping it: a picker that can't show the stored
  // rate either renders blank or silently re-prices the entry on save.
  if (!rateOptions.some((o) => o.id === entry.service_rate_id)) {
    const amount = entry.service_rate_amount ?? '0';
    rateOptions.push({
      id: entry.service_rate_id,
      amount,
      rate_type: entry.service_rate_type ?? 'hourly',
      is_product_sale: entry.service_rate_is_product_sale ?? false,
      label: `${entry.service_rate_name ?? `Rate #${entry.service_rate_id}`} — ${formatMoney(amount)} (current rate)`,
    });
  }

  // Fill the picker only when nothing is chosen — never replace a live choice.
  // Changing the project resets `rateId` to 0, which is what re-arms this.
  const firstRateId = rateOptions[0]?.id ?? 0;
  useEffect(() => {
    if (rateId !== 0 || ratesQ.isPending || firstRateId === 0) return;
    setRateId(firstRateId);
    setFee(rateOptions[0]!.amount);
  }, [rateId, ratesQ.isPending, firstRateId]);

  const selectedRate = rateOptions.find((o) => o.id === rateId);
  const flat = isFlatRate(selectedRate);
  // A product-sale rate (0059) makes the entry a SALE: a sale date, no time or
  // hours, and the device it sold.
  const sale = selectedRate?.is_product_sale === true;
  // A flat fee bills once at the entry's fee; hourly is hours × rate.
  const subtotal = !selectedRate
    ? null
    : flat
      ? (/^\d+(\.\d{1,2})?$/.test(fee) ? Number(fee) : null)
      : (/^\d+(\.\d{1,2})?$/.test(range.hours) ? Number(range.hours) * Number(selectedRate.amount) : null);

  const billed = entry.invoice_id !== null;
  const wasApproved = entry.approved_at !== null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTopError(null);
    setFieldErrors({});

    if (projectId <= 0) return setFieldErrors({ project_id: 'Select a project' });
    if (rateId <= 0) return setFieldErrors({ service_rate_id: 'Select a rate' });
    if (range.start === null) return setFieldErrors({ date: 'Pick a date' });
    if (!sale && range.end !== null && range.end <= range.start) {
      return setFieldErrors({ ended_at_epoch: 'End time must be after the start time' });
    }
    // Clearing the duration used to spring back to 0.10 on blur; now it stays
    // empty, so catch it here rather than letting the API's format check
    // return it as a 400.
    // A flat fee bills once, so its duration may stay blank (0057).
    const hoursBlank = sale || range.hours.trim() === '';
    if (!(flat && hoursBlank)
        && (!/^\d+(\.\d{1,2})?$/.test(range.hours.trim()) || Number(range.hours) <= 0)) {
      return setFieldErrors({ hours: 'Enter a duration, or an end time' });
    }
    if (flat && !/^\d+(\.\d{1,2})?$/.test(fee.trim())) {
      return setFieldErrors({ flat_amount: 'Enter the fee for this entry' });
    }
    if (description.trim().length === 0) {
      return setFieldErrors({ description: 'Add a description' });
    }

    try {
      // The API diffs against the stored row, so sending the full set is
      // safe — unchanged fields are dropped before the UPDATE.
      await update.mutateAsync({
        id: entry.id,
        project_id: projectId,
        ...(canReassign ? { user_id: userId } : {}),
        service_rate_id: rateId,
        hours: hoursBlank ? null : range.hours,
        // Hourly: the API drops a stored fee itself when the rate changes type.
        ...(flat ? { flat_amount: fee.trim() } : {}),
        ...(sale ? { asset_id: assetId } : {}),
        matter_id: matterId,
        // A sale is dated, not timed — no span, and the date at local midnight.
        date: sale ? startOfLocalDay(range.start) : range.start,
        description: description.trim(),
        started_at_epoch: sale || range.end === null ? null : range.start,
        ended_at_epoch: sale ? null : range.end,
      });
      onClose();
    } catch (err) {
      // Two 409s now: already invoiced, or the chosen drive is sold elsewhere
      // (0058). The API's message names which.
      if (err instanceof ApiError && err.status === 409) {
        setTopError(apiErrorText(err, 'This entry is already on an invoice — void the invoice first.'));
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setTopError("You don't have permission to make this change.");
        return;
      }
      if (err instanceof ApiError && err.status === 404) {
        setTopError('That time entry no longer exists.');
        return;
      }
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as
          | { error?: string; details?: { fieldErrors?: Record<string, string[]> } }
          | null;
        const fe = body?.details?.fieldErrors;
        if (fe) {
          const mapped: Record<string, string> = {};
          for (const [k, v] of Object.entries(fe)) {
            if (Array.isArray(v) && v.length > 0) mapped[k] = v[0]!;
          }
          if (Object.keys(mapped).length > 0) {
            setFieldErrors(mapped);
            return;
          }
        }
        setTopError(body?.error ?? 'Please check the highlighted fields.');
        return;
      }
      setTopError(apiErrorText(err, 'Could not save the changes.'));
    }
  };

  const busy = update.isPending;
  const projects = projectsQ.data?.projects ?? [];
  const users = usersQ.data?.users ?? [];

  return (
    <Modal
      title="Edit Time Entry"
      open={open}
      onClose={onClose}
      busy={busy}
      headerActions={
        <span
          className={`etm-pill ${
            billed ? 'etm-billed' : wasApproved ? 'etm-approved' : 'etm-unbilled'
          }`}
        >
          {billed ? `Invoice #${entry.invoice_id}` : wasApproved ? 'Approved' : 'Unbilled'}
        </span>
      }
      footer={
        <>
          <button type="button" className="btn-charcoal-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="edit-time-entry-form" className="btn-cyan-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <form id="edit-time-entry-form" className="etm-form" onSubmit={onSubmit} noValidate>
        {wasApproved && (
          <p className="etm-note">
            This entry is approved. Changing the project, rate, fee, times or hours clears the
            approval — it returns to Unbilled and needs approving again. Editing only the
            description leaves the approval in place.
          </p>
        )}

        <div className="etm-row">
          <label className="etm-field">
            <span>Project *</span>
            <select
              className="form-input"
              value={projectId}
              onChange={(e) => {
                setProjectId(Number(e.target.value));
                setRateId(0); // re-derives from the new project's rate sheet
                setMatterId(null); // a matter belongs to one project
              }}
              disabled={busy}
            >
              <option value={0}>Select a project…</option>
              <ProjectOptions projects={projects} />
            </select>
            {fieldErrors['project_id'] && (
              <span className="field-error">{fieldErrors['project_id']}</span>
            )}
          </label>

          <label className="etm-field">
            <span>Who *</span>
            {canReassign ? (
              <select
                className="form-input"
                value={userId}
                onChange={(e) => setUserId(Number(e.target.value))}
                disabled={busy || usersQ.isPending}
              >
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.display_name}
                  </option>
                ))}
              </select>
            ) : (
              <span className="etm-readonly">
                {entry.user_display_name ?? `User #${entry.user_id}`}
              </span>
            )}
            {fieldErrors['user_id'] && (
              <span className="field-error">{fieldErrors['user_id']}</span>
            )}
          </label>
        </div>

        <MatterSelect
          className="etm-field"
          projectId={projectId || null}
          value={matterId}
          onChange={setMatterId}
          disabled={busy}
          error={fieldErrors['matter_id']}
        />

        <label className="etm-field">
          <span>Service rate *</span>
          <select
            className="form-input"
            value={rateId}
            onChange={(e) => {
              const id = Number(e.target.value);
              setRateId(id);
              // A different service starts from its own default fee.
              setFee(rateOptions.find((o) => o.id === id)?.amount ?? '');
            }}
            disabled={busy || projectId <= 0 || ratesQ.isPending}
          >
            <option value={0}>
              {projectId <= 0
                ? 'Pick a project first'
                : ratesQ.isPending
                ? 'Loading rates…'
                : 'Select a rate…'}
            </option>
            {rateOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          {fieldErrors['service_rate_id'] && (
            <span className="field-error">{fieldErrors['service_rate_id']}</span>
          )}
        </label>

        {flat && (
          <label className="etm-field">
            <span>Flat fee for this entry *</span>
            <MoneyInput value={fee} onChange={setFee} disabled={busy} aria-label="Flat fee for this entry" />
            {fieldErrors['flat_amount'] && (
              <span className="field-error">{fieldErrors['flat_amount']}</span>
            )}
          </label>
        )}

        {sale && (
          <SoldAssetPicker
            className="etm-field"
            id={`etm-${entry.id}-asset`}
            value={assetId}
            onChange={setAssetId}
            current={entry.asset_id != null
              ? { id: entry.asset_id, name: entry.asset_name ?? `Asset #${entry.asset_id}` }
              : null}
            disabled={busy}
            error={fieldErrors['asset_id']}
          />
        )}

        {sale ? (
          <SaleDateField
            className="etm-field"
            id={`etm-${entry.id}-sale-date`}
            value={range.start}
            onChange={(v) => setRange({ ...range, start: v })}
            disabled={busy}
            error={fieldErrors['date']}
          />
        ) : (
          <TimeRangeFields
            value={range}
            onChange={setRange}
            disabled={busy}
            idPrefix={`etm-${entry.id}`}
            flatFee={flat}
            errors={{
              start: fieldErrors['date'],
              end: fieldErrors['ended_at_epoch'] ?? fieldErrors['started_at_epoch'],
              hours: fieldErrors['hours'],
            }}
          />
        )}

        <label className="etm-field">
          <span>Description *</span>
          <textarea
            className="form-input etm-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={10000}
            disabled={busy}
            placeholder="What did you work on?"
          />
          {fieldErrors['description'] && (
            <span className="field-error">{fieldErrors['description']}</span>
          )}
        </label>

        {subtotal !== null && (
          <div className="etm-subtotal">
            Line total <strong>{formatMoney(subtotal.toFixed(2))}</strong>
          </div>
        )}

        {topError && <p className="etm-error-banner" role="alert">{topError}</p>}
      </form>
    </Modal>
  );
}
